import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Menu, Calendar, Table2, User, ChevronDown } from 'lucide-react';
import HyperDX from '@hyperdx/browser';

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { UIStateProvider } from '@/core/UIStateProvider';
import { NotificationToast } from './notification-toast';
import { useCompanyProject, useProjectShipments, useUserProfile } from '@/core/api/queries';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Global error handling for all queries
      console.log(error, query);
    },
  }),
});

const DashboardContent = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: userProfile, isLoading: isUserProfileLoading } = useUserProfile();
  const { data: project, isLoading: isProjectLoading } = useCompanyProject(userProfile?.companyId);
  const { data: projectShipments, isLoading: isProjectShipmentsLoading } = useProjectShipments(project?.id);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isLoading = isUserProfileLoading || isProjectLoading || isProjectShipmentsLoading;

  const blockOnboardingAccess = useMemo(
    () => project?.id && projectShipments?.length > 0 && location.pathname === '/app/onboarding' && !isLoading,
    [project?.id, projectShipments?.length, location.pathname, isLoading],
  );

  const navigateToOnboarding = useMemo(
    () => (!project?.id || projectShipments?.length === 0) && !isLoading,
    [project?.id, projectShipments?.length, isLoading],
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (navigateToOnboarding) {
      navigate('/app/onboarding');
    } else if (blockOnboardingAccess) {
      navigate('/app/table');
    }
  }, [navigateToOnboarding, blockOnboardingAccess, navigate]);

  const handleMenuItemClick = () => {
    setDropdownOpen(false);
    if (sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (userProfile?.company) {
      HyperDX.setGlobalAttributes({
        userId: userProfile?.id,
        company: userProfile?.company?.name,
        // Any custom properties
      });
    }
  }, [userProfile]);

  const menuItems = [
    { icon: Table2, label: 'Table', path: '/app/table' },
    { icon: Calendar, label: 'Calendar', path: '/app/calendar' },
  ];

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col
        `}
      >
        {/* Header */}
        <Link to="/app/table" onClick={() => sidebarOpen && setSidebarOpen(false)}>
          <div className="h-14 p-4 border-b border-r border-gray-200">
            <div className="w-full relative flex items-center">
              <img src="/logo.png" alt="FreightTech.org Logo" className="h-8 w-auto mr-3" />
              <span className="text-lg font-semibold text-gray-900">FreightTech.org</span>
            </div>
          </div>
        </Link>

        {/* Navigation */}
        <div className="p-4 mb-8">
          <nav className="space-y-1">
            {menuItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                onClick={() => sidebarOpen && setSidebarOpen(false)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] transition-colors ${currentPath === item.path ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Ad Placeholders */}
        <div className="px-4 mb-4 flex-1 overflow-y-auto">
          {/* OFF-DOCK GDAŃSK Banner */}
          <a
            href="https://offdock.pl/"
            target="_blank"
            className="block w-full h-[110px] mb-4  rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, #FFF9E6 0%, #FFFAED 100%)',
            }}
          >
            <div className="relative w-full h-full p-3 flex flex-col justify-between">
              {/* Background container image - subtle */}
              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage:
                    'url(https://images.unsplash.com/photo-1494412651409-8963ce7935a7?w=400&h=300&fit=crop)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />

              {/* Content */}
              <div className="relative z-10 flex flex-col justify-between h-full">
                {/* Logo/Brand - centered */}
                <div className="flex items-center justify-center gap-2 mt-4">
                  <div className="w-5 h-5 bg-[#F5C518] rounded flex items-center justify-center flex-shrink-0">
                    <div className="w-3 h-3 border-2 border-black" />
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-[16px] text-gray-800 font-bold tracking-wide">OFF-DOCK</span>
                    <span className="text-[16px] text-[#E6B800] font-bold tracking-wide">GDAŃSK</span>
                  </div>
                </div>

                {/* Tagline */}
                <div className="text-center pb-1">
                  <div className="text-gray-600 text-[8px] uppercase tracking-wider">TWÓJ SKŁAD KONTENEROWY</div>
                </div>
              </div>
            </div>
          </a>

          {/* BALTIC HUB Banner */}
          <a
            href="https://baltichub.com/"
            target="_blank"
            className="block mb-4 w-full h-[110px] rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, #E3F2FD 0%, #DBEAFE 100%)',
            }}
          >
            <div className="relative w-full h-full p-3 flex flex-col justify-between">
              {/* Geometric pattern background */}
              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(59,130,246,0.1) 10px, rgba(59,130,246,0.1) 20px)',
                }}
              />

              {/* Content */}
              <div className="relative z-10 flex flex-col justify-between h-full">
                {/* Logo */}
                <div className="flex items-center justify-center mt-4">
                  <img src="/baltic-hub.png" alt="BalticHub" className="h-6 w-auto" />
                </div>

                {/* Tagline */}
                <div className="text-center pb-1">
                  <div className="text-gray-700 text-[9px] leading-tight px-1">
                    Najnowocześniejszy i największy
                    <br />
                    terminal kontenerowy w Polsce
                  </div>
                </div>
              </div>
            </div>
          </a>
          {/* AC PORATH - AGENCJA CELNA Banner */}
          <a
            href="https://acporath.com/"
            target="_blank"
            className="block w-full h-[110px] mb-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
            }}
          >
            <div className="relative w-full h-full p-3 flex flex-col justify-between">
              {/* Subtle truck/customs background */}
              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage:
                    'url(https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=400&h=300&fit=crop)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />

              {/* Content */}
              <div className="relative z-10">
                {/* Logo */}
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="flex items-center gap-0.5">
                    <div className="w-1 h-4 bg-[#E63946] rounded-sm" />
                    <div className="w-1 h-4 bg-[#0EA5E9] rounded-sm" />
                  </div>
                  <span className="text-[12px] text-gray-800 font-bold tracking-tight">AC PORATH</span>
                </div>

                {/* Main headline */}
                <div className="text-center mt-2">
                  <div className="text-gray-800 text-[13px] font-bold tracking-wide leading-tight">AGENCJA CELNA</div>
                </div>
              </div>

              {/* Tagline */}
              <div className="relative z-10 text-center">
                <div className="text-gray-700 text-[9px] leading-tight px-1">
                  Kompleksowe procedury
                  <br />
                  celne i obsługa formalności
                </div>
              </div>
            </div>
          </a>

          {/* ETA Banner */}
          <a
            href="https://open.spotify.com/show/4APTth3P6a599gebz1oBmV"
            target="_blank"
            className="block w-full h-[110px] mb-4  rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all overflow-hidden group bg-white"
          >
            <div className="relative w-full h-full p-3 flex flex-col justify-between">
              {/* Ship image positioned on the right */}
              <div
                className="absolute right-0 bottom-0 w-[100px] h-[70px] bg-contain bg-no-repeat bg-bottom-right opacity-90"
                style={{
                  backgroundImage: `url('/ship-image.png')`,
                  backgroundPosition: 'right bottom',
                }}
              />

              {/* Content */}
              <div className="relative z-10">
                {/* Logo */}
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-black text-[14px] font-bold tracking-tight">ETA</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#E63946]" />
                </div>

                {/* Tagline */}
                <div className="mt-3">
                  <div className="text-gray-900 text-[11px] font-semibold leading-tight">
                    Rozwijaj się
                    <br />z nami
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>

        {/* User Profile at Bottom */}
        <div className="border-t border-gray-200 p-4">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={toggleDropdown}
              className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                <span className="text-white font-semibold text-sm">{userProfile?.name?.charAt(0) || 'U'}</span>
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {userProfile?.name ?? 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {userProfile?.company?.name || 'Company'}
                </p>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${dropdownOpen ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {dropdownOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                {(userProfile?.role?.toLowerCase() ?? '').includes('super_admin') ? (
                  <>
                    {' '}
                    <Link
                      to="/app/profile"
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                      onClick={handleMenuItemClick}
                    >
                      <User className="w-4 h-4 mr-3 text-gray-400" />
                      Edit Profile
                    </Link>
                    <Link
                      to="/app/settings"
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                      onClick={handleMenuItemClick}
                    >
                      <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      System Management
                    </Link>
                    <Link
                      to="/app/maps"
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                      onClick={handleMenuItemClick}
                    >
                      <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                        />
                      </svg>
                      Maps
                    </Link>
                    <Link
                      to="/app/ships"
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                      onClick={handleMenuItemClick}
                    >
                      <svg
                        className="w-4 h-4 mr-3 text-gray-600"
                        fill="currentColor"
                        data-name="Layer 1"
                        id="Layer_1"
                        viewBox="0 0 80 80"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M78,62c-3,0-4.36,2.38-5.61,4.49S70.18,70,68.5,70a2.73,2.73,0,0,1-1.84-.71s0,0,0,0L75,38.34a1,1,0,0,0-.52-1.15L62,30.94V15.14C62,9.87,50.15,8,40,8S18,9.87,18,15.14v15.8L5.55,37.19A1,1,0,0,0,5,38.34l8.3,30.92s0,0,0,0A2.69,2.69,0,0,1,11.5,70c-1.69,0-2.59-1.33-3.89-3.51S4.94,62,2,62a1,1,0,0,0,0,2c1.68,0,2.59,1.33,3.89,3.51S8.55,72,11.5,72s4.36-2.38,5.61-4.49S19.31,64,21,64s2.6,1.33,3.89,3.51S27.55,72,30.49,72s4.36-2.38,5.61-4.49S38.3,64,40,64s2.59,1.33,3.89,3.51S46.55,72,49.49,72s4.36-2.39,5.61-4.49S57.31,64,59,64s2.59,1.33,3.89,3.51S65.55,72,68.5,72s4.36-2.38,5.61-4.49S76.31,64,78,64a1,1,0,0,0,0-2Zm-39,.1c-2.29.47-3.52,2.54-4.62,4.39C33.08,68.67,32.17,70,30.49,70s-2.59-1.33-3.89-3.51S23.94,62,21,62s-4.36,2.38-5.6,4.49l-.51.83L7.18,38.61,39,22.62Zm1.42-42A.92.92,0,0,0,40,20h0a.92.92,0,0,0-.41.1h0L20,29.93V15.14C20,12.31,29,10,40,10a59.22,59.22,0,0,1,14.19,1.5c3.69,1,5.81,2.29,5.81,3.64V29.93L40.45,20.11ZM64.6,66.49C63.35,64.38,61.93,62,59,62s-4.36,2.38-5.61,4.49S51.17,70,49.49,70s-2.59-1.33-3.89-3.51c-1.1-1.85-2.32-3.91-4.6-4.39V22.62l31.82,16-7.7,28.73Z"
                        />
                      </svg>
                      Ships
                    </Link>
                  </>
                ) : (
                  (userProfile?.role?.toLowerCase() ?? '').includes('admin') && (
                    <>
                      <Link
                        to="/app/maps"
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                        onClick={handleMenuItemClick}
                      >
                        <svg
                          className="w-4 h-4 mr-3 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                          />
                        </svg>
                        Maps
                      </Link>
                      <Link
                        to="/app/ships"
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                        onClick={handleMenuItemClick}
                      >
                        <svg
                          className="w-4 h-4 mr-3 text-gray-600"
                          fill="currentColor"
                          data-name="Layer 1"
                          id="Layer_1"
                          viewBox="0 0 80 80"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M78,62c-3,0-4.36,2.38-5.61,4.49S70.18,70,68.5,70a2.73,2.73,0,0,1-1.84-.71s0,0,0,0L75,38.34a1,1,0,0,0-.52-1.15L62,30.94V15.14C62,9.87,50.15,8,40,8S18,9.87,18,15.14v15.8L5.55,37.19A1,1,0,0,0,5,38.34l8.3,30.92s0,0,0,0A2.69,2.69,0,0,1,11.5,70c-1.69,0-2.59-1.33-3.89-3.51S4.94,62,2,62a1,1,0,0,0,0,2c1.68,0,2.59,1.33,3.89,3.51S8.55,72,11.5,72s4.36-2.38,5.61-4.49S19.31,64,21,64s2.6,1.33,3.89,3.51S27.55,72,30.49,72s4.36-2.38,5.61-4.49S38.3,64,40,64s2.59,1.33,3.89,3.51S46.55,72,49.49,72s4.36-2.39,5.61-4.49S57.31,64,59,64s2.59,1.33,3.89,3.51S65.55,72,68.5,72s4.36-2.38,5.61-4.49S76.31,64,78,64a1,1,0,0,0,0-2Zm-39,.1c-2.29.47-3.52,2.54-4.62,4.39C33.08,68.67,32.17,70,30.49,70s-2.59-1.33-3.89-3.51S23.94,62,21,62s-4.36,2.38-5.6,4.49l-.51.83L7.18,38.61,39,22.62Zm1.42-42A.92.92,0,0,0,40,20h0a.92.92,0,0,0-.41.1h0L20,29.93V15.14C20,12.31,29,10,40,10a59.22,59.22,0,0,1,14.19,1.5c3.69,1,5.81,2.29,5.81,3.64V29.93L40.45,20.11ZM64.6,66.49C63.35,64.38,61.93,62,59,62s-4.36,2.38-5.61,4.49S51.17,70,49.49,70s-2.59-1.33-3.89-3.51c-1.1-1.85-2.32-3.91-4.6-4.39V22.62l31.82,16-7.7,28.73Z"
                          />
                        </svg>
                        Ships
                      </Link>
                    </>
                  )
                )}
                <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                <button
                  onClick={() => {
                    localStorage.removeItem('_jwt_fto');
                    navigate('/');
                  }}
                  className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden transition-opacity duration-300"
          onClick={toggleSidebar}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header for mobile menu button */}
        <header className="lg:hidden bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-sm">FTO</span>
              </div>
              <span className="text-lg font-semibold text-gray-800 dark:text-white">FreightTech</span>
            </div>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <>
            <Outlet />
          </>
        </main>
      </div>
    </div>
  );
};

const Dashboard = () => {
  return (
    <QueryClientProvider client={queryClient}>
      {process.env.REACT_APP_ENV === 'local' && <ReactQueryDevtools initialIsOpen={false} />}
      <UIStateProvider>
        <NotificationToast />
        <DashboardContent />
      </UIStateProvider>
    </QueryClientProvider>
  );
};

export default Dashboard;
